'use client';

import { AtomicCategoryFacetSection } from '@components/Search/AtomicCategoryFacetSection';
import { AtomicPaginationSection } from '@components/Search/AtomicPaginationSection';
import { AtomicQueryRefinement } from '@components/Search/AtomicQueryRefinement';
import { AtomicResultsSectionV2 } from '@components/Search/AtomicResultsSection.v2';
import { AtomicStatusSection } from '@components/Search/AtomicStatusSection';
import { SearchResultsBanner } from '@components/Search/SearchResultsBanner';
import { SearchTitle } from '@components/Search/SearchTitle';
import { AtomicSearchContext } from '@contexts/coveo/AtomicSearchContext';
import {
  AtomicLayoutSection,
  AtomicNoResults,
  AtomicQueryError,
  AtomicSearchLayout,
} from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { Tag } from '@lib/types/common/Tag';
import { SearchResultsBanner as SearchResultsBannerType } from '@lib/types/search/SearchResultsBanner';
import { AtomicSearchInterfaceWrapper } from './AtomicSearchInterfaceWrapper';

export type SearchPageContentV2Props = {
  mappedSearchResultsBanners: SearchResultsBannerType[];
  mappedProductTags: Tag[] | undefined;
  paginationMicrocopy: MicrocopyFragment[];
};

export const SearchPageContentV2 = ({
  mappedSearchResultsBanners,
  mappedProductTags,
  paginationMicrocopy,
}: SearchPageContentV2Props) => {
  return (
    <div className="lg-container min-h-[25rem] pb-10 pt-4 lg:pb-16 lg:pt-12">
      <AtomicSearchInterfaceWrapper
        id="atomic-search-interface_search"
        shouldExecuteFirstSearch={true}
        reflectStateInUrl={true}
        context={AtomicSearchContext}
      >
        <style>
          {`
            /* V2 Layout: Fixed sidebar + fluid content */
            atomic-search-layout {
              display: flex !important;
              flex-direction: column !important;
              gap: 0 !important;
            }

            @media (min-width: 1024px) {
              atomic-search-layout {
                flex-direction: row !important;
                gap: 1.25rem !important;
              }
            }

            /* Sidebar: Hidden on mobile, fixed width on desktop */
            atomic-layout-section[section='facets'] {
              display: none !important;
            }

            @media (min-width: 1024px) {
              atomic-layout-section[section='facets'] {
                display: block !important;
                width: 260px !important;
                min-width: 260px !important;
                flex-shrink: 0 !important;
              }
            }

            @media (min-width: 1536px) {
              atomic-layout-section[section='facets'] {
                width: 280px !important;
                min-width: 280px !important;
              }
            }

            /* Main content: Full width on mobile, fluid on desktop */
            atomic-layout-section[section='main'] {
              flex: 1 !important;
              min-width: 0 !important;
              width: 100% !important;
            }

            /* Error/No results styling */
            atomic-no-results::part(icon), atomic-query-error::part(icon) {
              display: none;
            }

            atomic-query-error::part(title) {
              margin-top: unset;
            }

            @media (min-width: 768px) {
              atomic-query-error::part(title) {
                margin-top: -3rem;
              }
              atomic-no-results::part(no-results) {
                margin-top: -2rem;
              }
            }

            @media (min-width: 1024px) {
              atomic-query-error::part(title) {
                margin-top: -2rem;
              }
              atomic-no-results::part(no-results) {
                margin-top: unset;
              }
            }
          `}
        </style>
        <SearchResultsBanner
          context={AtomicSearchContext}
          banners={mappedSearchResultsBanners}
        />
        <SearchTitle context={AtomicSearchContext} />
        <AtomicSearchLayout>
          <AtomicLayoutSection section="facets">
            <AtomicCategoryFacetSection />
          </AtomicLayoutSection>
          <AtomicLayoutSection section="main">
            <div className="mb-6">
              <AtomicQueryRefinement context={AtomicSearchContext} />
            </div>
            <AtomicStatusSection />
            <AtomicResultsSectionV2
              productTags={mappedProductTags}
              context={AtomicSearchContext}
            />
            <AtomicPaginationSection
              paginationMicrocopySet={paginationMicrocopy}
              context={AtomicSearchContext}
            />
          </AtomicLayoutSection>
        </AtomicSearchLayout>
        <AtomicQueryError />
        <AtomicNoResults />
      </AtomicSearchInterfaceWrapper>
    </div>
  );
};
